import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AnalyticsService } from './analytics.service';
import { LogService } from './log.service';
import { Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// ============================================================================
// INTERFACES - Modèle unifié DailySales (kiosk + manual)
// ============================================================================

// Alias pour compatibilité avec ancien code
export interface ProductSale {
  productId: string;
  productName: string;
  recommended: number | null;
  prepared: number;
  sold: number;
  wasted?: number;
  hasRupture?: boolean;
  accuracy?: number;
  deviation?: number;
}

export interface ProductSaleEntry {
  productId: string;
  productName: string;
  quantityRecommended?: number;
  quantityPrepared: number;
  quantitySold: number;
  quantityWasted?: number;
  unitPrice: number;
  revenue?: number;
  wasteRate?: number;
  sellThroughRate?: number;
  hasRupture?: boolean;
  accuracyVsRecommendation?: number;
}

export interface ContextFlags {
  ruptureStock?: boolean;
  unusualAffluence?: boolean;
  weatherImpact?: boolean;
  localEvent?: boolean;
  technicalIssue?: boolean;
  differentLocation?: boolean;
}

export interface SaleSummary {
  totalRevenue: number;
  totalQuantitySold: number;
  totalQuantityPrepared: number;
  totalQuantityWasted: number;
  averageWasteRate: number;
  averageSellThroughRate: number;
  averageAccuracyVsRecommendation: number;
  productCount: number;
}

export interface DailySalesPayload {
  merchantType: 'fixed' | 'itinerant';
  source?: 'kiosk' | 'manual';
  saleDate?: string;
  serviceId?: string;
  serviceName?: string;
  eventId?: string;
  eventName?: string;
  products: ProductSaleEntry[];
  contextFlags?: ContextFlags;
  notes?: string;
  actualWeather?: string;
  actualAttendance?: string;
  actualDuration?: number;
  isDraft?: boolean;
}

export interface DailySales extends DailySalesPayload {
  _id: string;
  userId: string;
  summary: SaleSummary;
  isValidated: boolean;
  validatedAt?: string;
  usedForLearning: boolean;
  usedForLearningAt?: string;
  linkedRecommendationId?: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface PendingDate {
  date: string;
  serviceId: string;
  serviceName: string;
  status: 'pending' | 'draft' | 'completed';
}

export interface PendingEvent {
  _id: string;
  name: string;
  eventDate: string;
  eventType: string;
  location?: any;
  status: 'pending' | 'draft' | 'completed';
}

export interface DailySalesListResponse {
  data: DailySales[];
  total: number;
  page: number;
  limit: number;
}

export interface DailySalesStats {
  period: string;
  startDate: string;
  endDate: string;
  overall: {
    totalRevenue: number;
    totalSales: number;
    averageAccuracy: number;
    averageWasteRate: number;
  };
  byProduct: Array<{
    productId: string;
    productName: string;
    totalSold: number;
    totalRevenue: number;
    averageWasteRate: number;
  }>;
  byService: Array<{
    serviceId: string;
    serviceName: string;
    totalRevenue: number;
    salesCount: number;
  }>;
}

// ============================================================================
// SERVICE - Gestion unifiée des ventes
// ============================================================================

@Injectable({
  providedIn: 'root',
})
export class DailySalesService {
  private apiUrl = `${environment.apiUrl}/daily-sales`;

  // État pour le brouillon courant
  private _currentDraft = signal<DailySales | null>(null);
  readonly currentDraft = this._currentDraft.asReadonly();

  // État pour la liste des ventes
  private _salesList = signal<DailySales[]>([]);
  readonly salesList = this._salesList.asReadonly();

  // État pour les stats
  private _stats = signal<DailySalesStats | null>(null);
  readonly stats = this._stats.asReadonly();

  private analytics = inject(AnalyticsService);
  private log = inject(LogService);

  constructor(private http: HttpClient) {}

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Créer une nouvelle vente
   */
  create(payload: DailySalesPayload): Observable<DailySales> {
    return this.http.post<DailySales>(this.apiUrl, payload).pipe(
      tap((sale) => {
        this.log.debug('DailySales created', { saleId: sale._id });
        if (!payload.isDraft) {
          this.analytics.trackVente(
            sale.summary?.totalRevenue ?? 0,
            payload.source ?? 'manual',
            payload.products.length,
          );
          this.refreshList(payload.merchantType);
        } else {
          this._currentDraft.set(sale);
        }
      }),
    );
  }

  /**
   * Récupérer une vente par ID
   */
  getById(id: string): Observable<DailySales> {
    return this.http.get<DailySales>(`${this.apiUrl}/${id}`);
  }

  /**
   * Liste des ventes avec filtres
   */
  list(params?: {
    merchantType?: 'fixed' | 'itinerant';
    source?: 'kiosk' | 'manual';
    from?: string;
    to?: string;
    serviceName?: string; // Nom du service (pas l'ID)
    isDraft?: boolean;
    isValidated?: boolean;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Observable<DailySalesListResponse> {
    let httpParams = new HttpParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Mapper from/to vers startDate/endDate pour le backend
          if (key === 'from') {
            httpParams = httpParams.set('startDate', String(value));
          } else if (key === 'to') {
            httpParams = httpParams.set('endDate', String(value));
          } else {
            httpParams = httpParams.set(key, String(value));
          }
        }
      });
    }

    return this.http
      .get<DailySalesListResponse>(this.apiUrl, { params: httpParams })
      .pipe(
        tap((response) => {
          this._salesList.set(response.data);
        }),
      );
  }

  /**
   * Mettre à jour une vente
   */
  update(
    id: string,
    updates: Partial<DailySalesPayload>,
  ): Observable<DailySales> {
    return this.http.put<DailySales>(`${this.apiUrl}/${id}`, updates).pipe(
      tap((sale) => {
        this.log.debug('DailySales updated', { saleId: sale._id });
        this.refreshList(sale.merchantType);
      }),
    );
  }

  /**
   * Supprimer une vente
   */
  delete(id: string): Observable<{ success: boolean; hardDelete?: boolean }> {
    return this.http
      .delete<{
        success: boolean;
        hardDelete?: boolean;
      }>(`${this.apiUrl}/${id}`)
      .pipe(
        tap(() => {
          this.analytics.trackVenteDelete();
          this._salesList.update((list) => list.filter((s) => s._id !== id));
        }),
      );
  }

  // ============================================================================
  // VALIDATION
  // ============================================================================

  /**
   * Valider un brouillon (le marque comme validé et déclenche le learning)
   */
  validate(id: string): Observable<{
    success: boolean;
    dailySales: DailySales;
    learningUpdated: boolean;
  }> {
    return this.http
      .post<{
        success: boolean;
        dailySales: DailySales;
        learningUpdated: boolean;
      }>(`${this.apiUrl}/${id}/validate`, {})
      .pipe(
        tap((response) => {
          this.log.debug('DailySales validated', {
            learningUpdated: response.learningUpdated,
          });
          if (response.dailySales) {
            this.refreshList(response.dailySales.merchantType);
            this._currentDraft.set(null);
          }
        }),
      );
  }

  // ============================================================================
  // STATS
  // ============================================================================

  /**
   * Obtenir les statistiques agrégées
   */
  getStats(params?: {
    period?: 'day' | 'week' | 'month' | 'year';
    from?: string;
    to?: string;
    merchantType?: 'fixed' | 'itinerant';
  }): Observable<DailySalesStats> {
    let httpParams = new HttpParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }

    return this.http
      .get<DailySalesStats>(`${this.apiUrl}/stats`, { params: httpParams })
      .pipe(
        tap((stats) => {
          this._stats.set(stats);
        }),
      );
  }

  // ============================================================================
  // PENDING MANAGEMENT (dates/events sans saisie)
  // ============================================================================

  /**
   * Obtenir les dates en attente de saisie (marchands fixes)
   */
  getPendingDates(daysBack: number = 7): Observable<PendingDate[]> {
    return this.http.get<PendingDate[]>(`${this.apiUrl}/pending-dates`, {
      params: { daysBack: String(daysBack) },
    });
  }

  /**
   * Obtenir les événements en attente de saisie (marchands itinérants)
   */
  getPendingEvents(): Observable<PendingEvent[]> {
    return this.http.get<PendingEvent[]>(`${this.apiUrl}/pending-events`);
  }

  // ============================================================================
  // DRAFT MANAGEMENT
  // ============================================================================

  /**
   * Récupérer le brouillon courant (pour une date/service spécifique)
   */
  getDraft(params: {
    saleDate: string;
    serviceId?: string;
    eventId?: string;
  }): Observable<DailySales | null> {
    let httpParams = new HttpParams().set('saleDate', params.saleDate);

    if (params.serviceId) {
      httpParams = httpParams.set('serviceId', params.serviceId);
    }
    if (params.eventId) {
      httpParams = httpParams.set('eventId', params.eventId);
    }

    return this.http
      .get<DailySales | null>(`${this.apiUrl}/draft`, { params: httpParams })
      .pipe(
        tap((draft) => {
          this._currentDraft.set(draft);
        }),
        catchError(() => {
          this._currentDraft.set(null);
          return of(null);
        }),
      );
  }

  /**
   * Sauvegarder un brouillon
   */
  saveDraft(payload: DailySalesPayload): Observable<DailySales> {
    return this.create({ ...payload, isDraft: true });
  }

  // ============================================================================
  // KIOSK INTEGRATION
  // ============================================================================

  /**
   * Ajouter une vente depuis le kiosk (temps réel)
   */
  addKioskSale(params: {
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    serviceName?: string;
    eventId?: string;
  }): Observable<DailySales> {
    return this.http.post<DailySales>(`${this.apiUrl}/kiosk`, params);
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Rafraîchir la liste des ventes
   */
  refreshList(merchantType?: 'fixed' | 'itinerant'): void {
    this.list({ merchantType, isDraft: false }).subscribe({
      error: (err) =>
        this.log.error('Erreur rafraîchissement liste', {
          error: (err as Error).message,
        }),
    });
  }

  /**
   * Réinitialiser le brouillon
   */
  clearDraft(): void {
    this._currentDraft.set(null);
  }

  /**
   * Calculer les métriques pour un produit
   */
  calculateProductMetrics(
    product: Partial<ProductSaleEntry>,
  ): Partial<ProductSaleEntry> {
    const prepared = product.quantityPrepared || 0;
    const sold = product.quantitySold || 0;
    const unitPrice = product.unitPrice || 0;
    const recommended = product.quantityRecommended;

    const wasted = Math.max(0, prepared - sold);
    const revenue = sold * unitPrice;
    const wasteRate = prepared > 0 ? (wasted / prepared) * 100 : 0;
    const sellThroughRate = prepared > 0 ? (sold / prepared) * 100 : 0;

    let accuracyVsRecommendation: number | undefined;
    if (recommended && recommended > 0) {
      const deviation = Math.abs(sold - recommended) / recommended;
      accuracyVsRecommendation = Math.max(0, 100 - deviation * 100);
    }

    return {
      ...product,
      quantityWasted: wasted,
      revenue,
      wasteRate,
      sellThroughRate,
      accuracyVsRecommendation,
      hasRupture: sold >= prepared && prepared > 0,
    };
  }

  /**
   * Calculer le résumé pour une liste de produits
   */
  calculateSummary(products: ProductSaleEntry[]): SaleSummary {
    const totalRevenue = products.reduce(
      (sum, p) => sum + (p.revenue || p.quantitySold * p.unitPrice),
      0,
    );
    const totalQuantitySold = products.reduce(
      (sum, p) => sum + p.quantitySold,
      0,
    );
    const totalQuantityPrepared = products.reduce(
      (sum, p) => sum + p.quantityPrepared,
      0,
    );
    const totalQuantityWasted = products.reduce(
      (sum, p) => sum + (p.quantityWasted || 0),
      0,
    );

    const averageWasteRate =
      totalQuantityPrepared > 0
        ? (totalQuantityWasted / totalQuantityPrepared) * 100
        : 0;

    const averageSellThroughRate =
      totalQuantityPrepared > 0
        ? (totalQuantitySold / totalQuantityPrepared) * 100
        : 0;

    const productsWithAccuracy = products.filter(
      (p) => p.accuracyVsRecommendation !== undefined,
    );
    const averageAccuracyVsRecommendation =
      productsWithAccuracy.length > 0
        ? productsWithAccuracy.reduce(
            (sum, p) => sum + (p.accuracyVsRecommendation || 0),
            0,
          ) / productsWithAccuracy.length
        : 0;

    return {
      totalRevenue,
      totalQuantitySold,
      totalQuantityPrepared,
      totalQuantityWasted,
      averageWasteRate,
      averageSellThroughRate,
      averageAccuracyVsRecommendation,
      productCount: products.length,
    };
  }

  /**
   * Validation: vérifier que sold <= prepared
   */
  validateProducts(products: ProductSale[]): string | null {
    for (const product of products) {
      if (product.sold > product.prepared) {
        return `Quantité vendue (${product.sold}) ne peut pas dépasser préparé (${product.prepared}) pour ${product.productName}`;
      }
    }
    return null;
  }
}
